/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { IronA11yKeysBehavior } from "@polymer/iron-a11y-keys-behavior/iron-a11y-keys-behavior.js";
import "@polymer/iron-flex-layout/iron-flex-layout.js";
import "@polymer/iron-selector/iron-selector.js";
import "@polymer/paper-icon-button/paper-icon-button.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import { FlattenedNodesObserver } from "@polymer/polymer/lib/utils/flattened-nodes-observer.js";
import { mixinBehaviors } from "@polymer/polymer/lib/legacy/class.js";
import { events } from "@nuskin/ns-util";
import template from "./ns-skeleton-carousel.html";

/**
 * `skeleton-carousel`
 *
 * @extends Polymer.Element
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class NSSkeletonCarousel extends mixinBehaviors(
  [IronA11yKeysBehavior],
  PolymerElement
) {
  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  /**
   * @return {string}
   */
  static get is() {
    return "ns-skeleton-carousel";
  }

  /**
   * @return {object}
   */
  static get properties() {
    return {
      /**
       * Indicates if the carousel is being animated (Transition)
       */
      animating: {
        type: Boolean,
        value: false,
        readOnly: true,
        reflectToAttribute: true,
        notify: true
      },
      /**
       * Change slides automatically
       */
      auto: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        observer: "_autoAnimate"
      },
      /**
       * Carousel direction (horizontal or vertical)
       */
      direction: {
        type: String,
        value: "horizontal",
        reflectToAttribute: true
      },
      /**
       * Disables component
       */
      disabled: {
        type: Boolean,
        value: false
      },
      /**
       * Show navigation bars
       */
      bars: {
        type: Boolean,
        value: false
      },
      /**
       * Disable swipe functionality
       */
      disableSwipe: {
        type: Boolean,
        value: false
      },
      /**
       * Disable keyboard navigation
       */
      disableKeys: {
        type: Boolean,
        value: false
      },
      /**
       * Auto play interval time in milliseconds
       */
      duration: {
        type: Number,
        value: 4000,
        observer: "_animationDuration"
      },
      /**
       * Determines if the carousel should be looped.
       * This affects the controls and the drag and drop functionality.
       * Set to `true` if you need to loop the slides.
       */
      loop: {
        type: Boolean,
        value: false
      },
      /**
       * Show navigation next/prev buttons
       */
      nav: {
        type: Boolean,
        value: false
      },
      /**
       * Selected slide index
       */
      selected: {
        type: Number,
        value: 0,
        notify: true,
        reflectToAttribute: true,
        observer: "_selectedObserver"
      },
      /**
       * Selected item
       */
      selectedItem: {
        type: Object,
        notify: true,
        readOnly: true
      },
      /**
       * The user is swiping
       */
      swiping: {
        type: Boolean,
        value: false,
        notify: true,
        readOnly: true,
        reflectToAttribute: true
      },
      /**
       * tabindex
       */
      tabindex: {
        type: Number,
        value: 0,
        reflectToAttribute: true
      },
      /**
       * Total number of slides
       */
      total: {
        type: Number,
        value: 0,
        notify: true,
        reflectToAttribute: true,
        readOnly: true
      },
      /**
       * Returns true when the carousel has reached the last slide.
       */
      end: {
        type: Boolean,
        value: false,
        notify: true,
        readOny: true,
        reflectToAttribute: true,
        computed: "_computeEnd(total, selected)"
      },
      /**
       * Determines if the next button should be displayed.
       */
      showNext: {
        type: Boolean,
        value: false,
        computed: "_computeShowNext(disabled, total, selected, loop)",
        notify: true,
        reflectToAttribute: true,
        readOnly: true
      },
      /**
       * Determines if the previous button should be displayed.
       */
      showPrev: {
        type: Boolean,
        value: false,
        computed: "_computeShowPrev(disabled, selected, loop)",
        notify: true,
        reflectToAttribute: true,
        readOnly: true
      },
      /**
       * Auto interval
       */
      _autoInterval: {
        type: Object
      },
      /**
       * Children elements
       */
      _children: {
        type: Array,
        value: [],
        observer: "_childrenObserver"
      },
      /**
       * Show navigation controls
       */
      _controls: {
        type: Boolean,
        value: false,
        readOnly: true,
        computed: "_computeControls(bars, nav, total)"
      },
      /**
       * Returns an array with the slides.
       * This will be used to create the bar controls.
       */
      _bars: {
        type: Array,
        computed: "_computeBars(total)",
        readOnly: true,
        value: []
      },
      /**
       * Icon for the previous button
       */
      _iconPrev: {
        type: String,
        value: "hardware:keyboard-arrow-left",
        readOnly: true,
        computed: "_computeIconPrev(direction)"
      },
      /**
       * Icon for the next button
       */
      _iconNext: {
        type: String,
        value: "hardware:keyboard-arrow-right",
        readOnly: true,
        computed: "_computeIconNext(direction)"
      },
      /**
       * Icon for the next button
       */
      _containerHeight: {
        type: String,
        value: "height: 100%;",
        computed: "_computeContainerHeight(direction, total)",
        readOnly: true
      },
      /**
       * X Axis position (0 - 1)
       */
      _x: {
        type: Number,
        value: 0
      },
      /**
       * Y Axis position (0 - 1)
       */
      _y: {
        type: Number,
        value: 0
      },
      analyticsActive: {
        type: Boolean,
        value: false
      },
      labels: {
        type: Array,
        value: []
      },
      heroIds: {
        type: Array,
        value: []
      },
      viewNotificationActive: {
        type: Array,
        value: [true]
      },
      carouselLocation: String,
      delayTimer: {
        type: String,
        value: "delay-timer"
      }
    };
  }

  /**
   * Ready event
   */
  connectedCallback() {
    super.connectedCallback();
    const container = this.shadowRoot.querySelector("slot");
    new FlattenedNodesObserver(container, () => {
      this._children = FlattenedNodesObserver.getFlattenedNodes(this).filter(
        (n) =>
          n.nodeType === Node.ELEMENT_NODE &&
          n.nodeName !== "DOM-REPEAT" &&
          n.nodeName !== "TEMPLATE"
      );
      if (this._children.length <= 1) this.disableSwipe = true;
      this._setTopArrow();
    });
    // Handle transition end
    const items = this.shadowRoot.querySelector("#items");
    items.addEventListener("transitionend", (e) => {
      e.preventDefault();
      if (this.swiping || e.propertyName !== "transform") {
        return;
      }
      this._setAnimating(false);
    });
    // this.selected = 0;
    window.addEventListener("resize", (e) => {
      this._resize(e);
    });
    events.subscribe(events.shop.NS_HERO_CLICKED, (response) => {
      this._sendClickedAnalytics(response.data);
    });
  }

  /**
   * @return {Array}
   */
  static get observers() {
    return ["_translateObserver(direction, selected, _x, _y)"];
  }

  /**
   * Key bindings
   * @return {object}
   */
  get keyBindings() {
    return {
      "left up": "_prevKey",
      "right down": "_nextKey"
    };
  }

  /**
   * Children change observer
   *
   * @param {array} children
   * @private
   */
  _childrenObserver(children) {
    this._setTotal(children.length);
    if (children.length > 1) {
      this.viewNotificationActive.push(true);
    }
    this._lazyContent(0);
    if (children.length >= 1) this._lazyContent(1);
  }

  /**
   * Go to the previous slide with keybindings
   *
   * @param {object} event
   * @private
   * @return {*}
   */
  _prevKey(event) {
    if (this.disableKeys || this.disabled) return;
    event.preventDefault();
    return this.prev();
  }

  /**
   * Go to the next slide with keybindings
   *
   * @param {object} event
   * @private
   * @return {*}
   */
  _nextKey(event) {
    if (this.disableKeys || this.disabled) return;
    event.preventDefault();
    return this.next();
  }

  /**
   * Observer on auto to turn on and off the feature
   *
   * @param {boolean} auto
   * @private
   */
  _autoAnimate(auto) {
    if (auto) {
      this._autoInterval = setInterval(() => {
        this.next();
      }, this.duration);
    } else {
      clearInterval(this._autoInterval);
    }
  }

  /**
   * Go to next slide
   */
  next() {
    if (this.disabled) return;
    const element = this.shadowRoot.querySelector("#container");
    if (this.selected < --this.total || this.loop) {
      this._setAnimating(true);
      element.selectNext();
    }
  }

  /**
   * Go to previous slide
   */
  prev() {
    if (this.disabled) return;
    const element = this.shadowRoot.querySelector("#container");
    if (this.selected > 0 || this.loop) {
      this._setAnimating(true);
      element.selectPrevious();
    }
  }

  /**
   * Switch the icon if the item is selected
   *
   * @param {number} item
   * @param {number} selected
   * @return {string}
   * @private
   */
  _iconBar(item, selected) {
    item--;
    let icon = "icons:radio-button-unchecked";
    if (Number(item) === Number(selected)) icon = "icons:radio-button-checked";
    return icon;
  }

  /**
   * Listen for changes on the selected item
   *
   * @param {number} newValue
   * @param {number} oldValue
   * @private
   */
  _selectedObserver(newValue, oldValue) {
    this._lazyContent(newValue);
    let selectedChild = this._children[newValue];
    this._setSelectedItem(selectedChild);
    if (newValue < --this.total) {
      this._lazyContent(++newValue);
    }
    // Reset auto
    this._autoAnimate(false);
    this._autoAnimate(this.auto);
    if (this._children) this._setTopArrow(this._children.length);
    this._sendViewedAnalytics();
  }

  /**
   * Lazy load content for the given slider number
   * Replaces 'data-src' attribute to 'src'.
   *
   * @param {number} selected
   * @private
   */
  _lazyContent(selected) {
    const child = this._children[selected];
    if (!child) return;
    // lazy load first level content
    if (child.hasAttribute("data-src")) {
      child.src = child.getAttribute("data-src");
      child.removeAttribute("data-src");
    }
    // Lazy load child content
    const content = child.querySelectorAll("[data-src]");
    if (content.length <= 0) return;
    let i = 0;
    for (i; i < content.length; ++i) {
      content[i].src = content[i].getAttribute("data-src");
      content[i].removeAttribute("data-src");
    }
  }

  /**
   * Determine if it should show the next button
   *
   * @param {boolean} disabled
   * @param {number} total
   * @param {number} selected
   * @param {boolean} loop
   * @return {boolean}
   * @private
   */
  _computeShowNext(disabled, total, selected, loop) {
    if (disabled) return false;
    if (loop) return true;
    return selected < --total;
  }

  /**
   * Determine if it should show the next button
   *
   * @param {boolean} disabled
   * @param {number} selected
   * @param {boolean} loop
   * @return {boolean}
   * @private
   */
  _computeShowPrev(disabled, selected, loop) {
    if (disabled) return false;
    if (loop) return true;
    return selected > 0;
  }

  /**
   * Returns the bars array
   *
   * @param {number} total
   * @return {Array}
   * @private
   */
  _computeBars(total) {
    let array = [];
    if (total <= 0) return array;
    let i = 1;
    total = ++total;
    for (i; i < total; ++i) {
      array.push(i);
    }
    return array;
  }

  /**
   * Determines if the current item is the last one on the list
   *
   * @param {number} total
   * @param {number} selected
   * @return {boolean}
   * @private
   */
  _computeEnd(total, selected) {
    const status = selected === --total;
    if (status) this._event("end");
    return status;
  }

  /**
   * Returns a valid value
   *
   * @param {number} value
   * @return {*}
   * @private
   */
  _normalize(value) {
    if (value < 0) {
      value = 0;
    } else if (value > 1) {
      value = 1;
    }
    return value;
  }

  /**
   * Returns the percentage that will to position the icon
   *
   * @param {number|string} value
   * @return {string}
   * @private
   */
  _calcPercentage(value) {
    value = value.toFixed(2);
    return `${value * 100}%`;
  }

  /**
   * Listen for track and move the container
   *
   * @param {object} event
   * @private
   */
  _drag(event) {
    if (this.disableSwipe || this.disabled) {
      // Reset selected to original in case is changed while dragging
      this._x = 0;
      this._y = 0;
      this._setSwiping(false);
      return;
    }
    const width = this.offsetWidth;
    const height = this.offsetHeight;
    const x = event.detail.ddx ? event.detail.ddx : 0;
    const y = event.detail.ddy ? event.detail.ddy : 0;
    let finalX = x / width;
    let finalY = y / height;
    let finalLeft = 0;
    let finalTop = 0;

    if (this.direction === "horizontal") finalLeft = this._x + finalX;
    if (this.direction === "vertical") finalTop = this._y + finalY;

    let finalPositionLeft = Number(finalLeft);
    let finalPositionTop = Number(finalTop);
    // Prevent drag to the wrong direction if is not available
    if (!this.showPrev) {
      if (finalPositionLeft > 0) {
        finalPositionLeft = 0;
      }
      if (finalPositionTop > 0) {
        finalPositionTop = 0;
      }
    }
    if (!this.showNext) {
      if (finalPositionLeft < 0) {
        finalPositionLeft = 0;
      }
      if (finalPositionTop < 0) {
        finalPositionTop = 0;
      }
    }
    switch (event.detail.state) {
      case "track":
        this._autoAnimate(false);
        this._x = finalPositionLeft;
        this._y = finalPositionTop;
        this._setSwiping(true);
        break;
      case "end":
        this._autoAnimate(this.auto);
        this._x = 0;
        this._y = 0;
        this._setSwiping(false);
        if (finalPositionLeft >= 0.1 || finalPositionTop >= 0.1) {
          this.prev();
        } else if (finalPositionLeft <= -0.1 || finalPositionTop <= -0.1) {
          this.next();
        }
        break;
    }
  }

  /**
   * Compute icon previews
   *
   * @param {string|null} direction
   * @return {string}
   * @private
   */
  _computeIconPrev(direction) {
    if (direction === "vertical") return "icons:arrow-upward";
    return "icons:arrow-back";
  }

  /**
   * Compute icon next
   *
   * @param {string|null} direction
   * @return {string}
   * @private
   */
  _computeIconNext(direction) {
    if (direction === "vertical") return "icons:arrow-downward";
    return "icons:arrow-forward";
  }

  /**
   * Translate swipe observer
   *
   * @param {string} direction
   * @param {number} selected
   * @param {number} _x
   * @param {number} _y
   * @private
   */
  _translateObserver(direction, selected, _x, _y) {
    if (_x !== 0 || _y !== 0) {
      this._setAnimating(true);
    }
    let finalX = 0;
    let finalY = 0;
    let baseSelected = -Math.abs(selected);
    if (selected <= 0) baseSelected = 0;
    if (_x === 0 && _y === 0) {
      const percentage = this._calcPercentage(baseSelected);
      if (direction === "horizontal") finalX = percentage;
      if (direction === "vertical") finalY = percentage;
    } else {
      // add baseSelected to the original value of the drag value
      _x = baseSelected + _x;
      _y = baseSelected + _y;
      if (direction === "horizontal") {
        finalX = this._calcPercentage(_x);
      }
      if (direction === "vertical") {
        finalY = this._calcPercentage(_y);
      }
    }

    const element = this.shadowRoot.querySelector("#items");
    let transform = "";
    if (direction === "horizontal") {
      transform = `translateX(${finalX})`;
    }
    if (direction === "vertical") {
      transform = `translateY(${finalY})`;
    }
    element.style.transform = transform;
  }

  /**
   * Compute translate swipe
   *
   * @param {number} position
   * @param {number} selected
   * @return {number}
   * @private
   */
  _returnDragPosition(position, selected) {
    return position - selected;
  }

  /**
   * Compute items container height
   *
   * @param {string} direction
   * @param {number} total
   * @return {string}
   * @private
   */
  _computeContainerHeight(direction, total) {
    let height = "auto";
    if (direction === "vertical") height = this._calcPercentage(total);

    return `height: ${height};`;
  }

  /**
   * Compute Controls
   * Returns true if needs to display the control.s
   *
   * @param {boolean} bars
   * @param {boolean} nav
   * @param {number} total
   * @return {boolean}
   * @private
   */
  _computeControls(bars, nav, total) {
    return total > 1 && (bars || nav);
  }

  /**
   * Fire event
   *
   * @param {string} name
   * @param {string|boolean|null} detail
   * @private
   */
  _event(name, detail = null) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail: detail,
        bubbles: true,
        composed: true
      })
    );
  }

  _setTopArrow() {
    if (this.selected >= 0) {
      this.updateStyles({
        "--skeleton-carousel-top-arrow--left":
          (window.innerWidth / this._children.length / 2) *
            (this.selected * 2 + 1) +
          "px"
      });
    }
  }

  _animationDuration() {
    this.updateStyles({
      "--skeleton-carousel-duration": this.duration + "ms"
    });
  }

  _getLabel(i) {
    return this.labels ? this.labels[i] : "";
  }

  _resize(e) {
    this._setTopArrow();
  }

  _pause() {
    if (this.auto) {
      this.rememberAuto = true;
      this.auto = false;
    }
  }

  _play() {
    if (this.rememberAuto) this.auto = !this.auto;
  }

  _stop() {
    this.rememberAuto = false;
    this.auto = false;
  }

  //ANALYTICS FUNCTIONS
  _sendViewedAnalytics() {
    if (
      this.heroIds.length > 0 &&
      this.labels.length > 0 &&
      this.viewNotificationActive[this.selected]
    ) {
      let payload = {
        ecommerce: {
          promoView: {
            promotions: [
              {
                id: this.heroIds[this.selected],
                name: this.labels[this.selected],
                creative: this.carouselLocation,
                position: this.selected
              }
            ]
          }
        }
      };
      events.publish(events.shop.PROMOTION_IMPRESSION, payload);
      this.viewNotificationActive[this.selected] = false;
    } else {
      return;
    }
  }

  _sendClickedAnalytics(heroID) {
    let tempIndex = this.heroIds.indexOf(heroID);
    if (tempIndex > -1) {
      let payload = {
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: heroID,
                name: this.labels[tempIndex],
                creative: this.carouselLocation,
                position: tempIndex
              }
            ]
          }
        }
      };
      events.publish(events.shop.PROMOTION_CLICK, payload);
    } else {
      return;
    }
  }
}

window.customElements.define(NSSkeletonCarousel.is, NSSkeletonCarousel);
